import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, errMsg } from "../lib/api";
import { useOp, saveBlob } from "../lib/useOp";
import { useDataset, DatasetSelect, Tabs, PageHead, Kpi, fmt, pct, CorteBadge } from "../lib/lm";
import { Btn } from "../components/kit/Kit";
import { Explorer } from "../components/lm/Explorer";
import { Vintage } from "../components/lm/Vintage";
import { Desempeno } from "../components/lm/Desempeno";
import { Overview, Volumen, Pagos, Cohortes, Perdidas, Forecast, Analitica, Metodologia } from "../components/lm/RepSections";
import { useCopilotoFiltros } from "../lib/copiloto";

const TABS = [["overview", "Overview"], ["desempeno", "Desempeño"], ["explorer", "Portfolio Explorer"], ["vintage", "Vintage raw data"], ["volumen", "Volumen y tendencias"], ["pagos", "Pagos"], ["cohortes", "Cohortes"], ["perdidas", "Pérdidas y transiciones"],
  ["forecast", "Forecast"], ["analitica", "Analítica"], ["metodologia", "Metodología"]];

const Filtros = ({ f, setF, productos }) => (
  <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-[#E2DDD5] bg-white p-4" data-testid="rep-filtros">
    {[["cut", "Corte"], ["desde", "Desembolso desde"], ["hasta", "hasta"]].map(([k, l]) => (
      <label key={k} className="text-[10px] font-semibold uppercase tracking-widest text-[#8C867A]">{l}<input type="date" data-testid={`rep-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 block rounded-lg border border-[#E2DDD5] px-2 py-1.5 text-sm" /></label>))}
    <label className="text-[10px] font-semibold uppercase tracking-widest text-[#8C867A]">Producto<select data-testid="rep-producto" value={f.producto || ""} onChange={(e) => setF({ ...f, producto: e.target.value })} className="mt-1 block rounded-lg border border-[#E2DDD5] px-2 py-1.5 text-sm"><option value="">Todos</option>{productos.map((p) => <option key={p}>{p}</option>)}</select></label>
    <label className="text-[10px] font-semibold uppercase tracking-widest text-[#8C867A]">Persona<select data-testid="rep-tipo" value={f.tipo || ""} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="mt-1 block rounded-lg border border-[#E2DDD5] px-2 py-1.5 text-sm"><option value="">PF y PM</option><option value="PF">PF</option><option value="PM">PM</option></select></label>
    <div className="flex rounded-xl border border-[#E2DDD5] p-0.5" data-testid="rep-moneda">{["MXN", "USD"].map((m) => <button key={m} data-testid={`moneda-${m}`} onClick={() => setF({ ...f, moneda: m })} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${f.moneda === m ? "bg-[#111] text-white" : ""}`}>{m === "MXN" ? "LCCY (MXN)" : "USD"}</button>)}</div>
  </div>
);

export default function LmReporting() {
  const { info, ds, setDs } = useDataset();
  const [f, setF] = useState({ moneda: "MXN" });
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  useCopilotoFiltros("portfolio_explorer", (p) => { if (p === null) { setF({ moneda: "MXN" }); return; } if (p.tab) setTab(p.tab); const { tab: _t, ...r } = p; if (Object.keys(r).length) setF((x) => ({ ...x, ...r })); }, { filtros: f, pestana: tab, vista: "Portfolio Explorer" });
  const { run, bar, busy } = useOp("tape-progress");
  useEffect(() => {
    if (!info) return;
    http.get("/lm/reporting", { params: { dataset: ds, ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) } }).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setF((x) => ({ ...x, moneda: "MXN" })); });
  }, [ds, f, info]);
  const tape = async (formato) => {
    try {
      const r = await run(`Data tape ${formato.toUpperCase()}`, (o) => http.get("/lm/tape", { ...o, params: { dataset: ds, formato }, responseType: "blob" }));
      saveBlob(r.data, `data_tape_${ds}.${formato}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cur = d?.moneda;
  return (
    <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-10" data-testid="lm-reporting-page" style={{ animation: "rise .35s ease-out both" }}>
      <PageHead kicker="Trafalgar Credit Platform" title="Reporting & Funding">
        <CorteBadge iso={d?.cut || info?.cortes?.[ds]} testid="rep-corte" />
        <DatasetSelect info={info} ds={ds} setDs={setDs} />
        {["xlsx", "zip", "pdf"].map((x) => <Btn key={x} kind={x === "xlsx" ? "accent" : "secondary"} disabled={busy} onClick={() => tape(x)} data-testid={`tape-${x}`}>{x === "xlsx" ? "Data tape Excel" : x === "zip" ? "CSV (zip)" : "PDF ejecutivo"}</Btn>)}
      </PageHead>
      {bar}
      {!["explorer", "vintage", "desempeno"].includes(tab) && <Filtros f={f} setF={setF} productos={d?.productos || []} />}
      {!d ? <p className="text-sm text-[#8C867A]">Calculando…</p> : <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Kpi l="Saldo insoluto" v={fmt(d.overview.saldo, cur)} testid="rep-saldo" sub={`Corte ${d.cut}${d.tc ? ` · FIX ${d.tc}` : ""}`} />
          <Kpi l="Originado" v={fmt(d.overview.originado, cur)} testid="rep-originado" />
          <Kpi l="Créditos vigentes" v={`${d.overview.vigentes} / ${d.overview.creditos}`} testid="rep-creditos" />
          <Kpi l="PAR30" v={pct(d.overview.par30, 2)} testid="rep-par30" />
          <Kpi l="IMOR (90+)" v={pct(d.overview.imor, 2)} testid="rep-imor" />
          <Kpi l="Tasa ponderada" v={pct(d.overview.tasa_ponderada, 1)} testid="rep-tasa" />
        </div>
        <Tabs tabs={TABS} tab={tab} setTab={setTab} />
        {tab === "overview" && <Overview d={d} />}
        {tab === "desempeno" && <Desempeno ds={ds} />}
        {tab === "explorer" && <Explorer ds={ds} />}
        {tab === "vintage" && <Vintage ds={ds} />}
        {tab === "volumen" && <Volumen d={d} />}
        {tab === "pagos" && <Pagos d={d} />}
        {tab === "cohortes" && <Cohortes d={d} />}
        {tab === "perdidas" && <Perdidas d={d} />}
        {tab === "forecast" && <Forecast d={d} />}
        {tab === "analitica" && <Analitica d={d} />}
        {tab === "metodologia" && <Metodologia d={d} />}
      </>}
    </main>
  );
}
